import { getMenu } from '@/api/product'
import { getVenueQuietly } from '@/api/venueQuiet'
import { buildQrMenuContext, isStoreVenue, menuContextFromState, resolvedMenuIdOf } from './qrMenu'
import { menuEntry } from './qrEntry'
import { rememberLinkTables } from './linkTables'

/**
 * review R5：调用方手上没有门店设置（商品页 `_loadProduct`）时，「这家店关没关外带」静默问一次 —— 调用方只在地址带 `ta` 时问
 * （不带 ta 时这个设置不影响用餐方式；也不多等一拍，时序与以前相同）。`getVenueQuietly` 不经拦截器、不写 store、失败回 null。
 * 关了 ⇒ false（?ta 不算外带，与 venue-home / 购物车同口径）；开着 / 没写 / 取不到 ⇒ undefined（照今天：不知道就当没关）。
 */
async function takeawayAllowedFromVenue(venueId) {
  const venue = await getVenueQuietly(venueId)
  const dineIn = venue && venue.settings && venue.settings.dineInSettings
  return dineIn && dineIn.enableTakeaway === false ? false : undefined
}

/**
 * 读菜单接口回「Can not found menu.」之后（`menuRead.js readWithMenuRetry` 的第二步）：带这家店此刻的 context 重新请求一次 get-menu，
 * 存下新解析出的 menuId。不弹窗。
 *
 * 只给 store 里那家店做（路由上是 UUID 或这家店的 url_id 都算，`isStoreVenue`）：页面上的门店与 store 对不上时（直接打开别家店、
 * get-venue 还没回来的那一下）**什么都不动** —— 以前会先清空，A 店迟到的失败能把 B 店刚解析好的菜单抹掉（U-B6-1 review，Codex）。
 * 请求门店与 context 一律用 store 里的门店 UUID（deliveryInfo 等按 UUID 存）。
 *
 * 重新解析失败（网络 / 后端抖动）⇒ 放回原来那个 menuId：留空会让之后的下单既不带 menuId 也不带 context（当成老后端），
 * 放回去则读照样回「菜单不在用」（调用方的第二次读失败，不会不带菜单地读），下单照样带 menuId + context，
 * 后端按 B5-0 回落并带 menuFallback ⇒ 客户端走「菜单刚更新」（U-B6-2）。
 * get-menu 回来时门店已经换了 ⇒ 不写（迟到的响应不许把 A 店的菜单装进 B 店）。
 *
 * venue-home 不走这里 —— 它自己的 getData 本来就是「get-menu → get-product-list」成对重来。
 *
 * @param {Object} store Vuex store
 * @param {Object} [where]
 * @param {String} [where.venueId] 这一页的门店（解码后的路由参数，UUID 或 url_id）
 * @param {Object} [where.query] 路由 query
 * @param {String|Number} [where.table] 路由上的桌号
 * @param {Boolean} [where.takeawayAllowed] 这家店开着外带吗（`dineInSettings.enableTakeaway !== false`；调用方有就传，与 venue-home 同口径；
 *   没传、地址又带 ta ⇒ 这里静默问一次门店设置，review R5）
 * @param {Object} [where.route] 当前路由（N35-B2 review 第 2 轮 P2：Custom Flows 页面上不送 service）
 * @param {Object} [where.context] 直接给这一单的 context（U-B6-2：「菜单刚更新」之后按被拒那一单自己的用餐方式 / 桌号，
 *   也就是购物车此刻的 —— owner B6-Q7 = A）；照样过一遍 `buildQrMenuContext`，门店与合单的守卫照旧
 *   （N35-B2：它的 service 原样透传，只要还是堂食、值认得；没带就不带，不拿地址上的参数补；N35-B5：它的 menuLink 同样原样透传）
 * @returns {Promise<String>} 新的 menuId（没换成就是 ''）
 *
 * N35-B2：不给 context 时（商品页 / 购物车推荐）带这一次光顾扫的码 —— 与下单同一个来源（`qrEntry.js menuEntry`：当前地址上的进店参数，
 * 不读 vuex / 存储、不按路由名 / portal 猜，Q16；Custom Flows 页面不带，Q19；通用桌码不带顾客自己填的桌号，Q17），
 * 否则只设了 Counter / 桌号的菜单在这里会被解析成别的菜单。N35-B5：地址里的点单链接码一起带（`menuEntry` 现读）。
 * 只写回 menuId（store 里不存 service、不存码）。
 *
 * 多版本菜单批 L2：新的 get-menu 回包同样记一次链接桌号表（`linkTables.js rememberLinkTables`，与 venue-home `_getMenu` 同一口径：
 * 这次 context 带码且有回包 ⇒ 记回包里的 linkTables，没有就记成「已知没有」；没带码 / 请求失败 ⇒ 清掉）。
 * 链接被停用 / 菜单回落之后，购物车下拉因此回到全名册，不再拿旧表。门店已经换了 ⇒ 同 menuId 一样不写。
 */
export async function reresolveMenu(store, { venueId, query, table, takeawayAllowed, route, context: orderContext } = {}) {
  if (!isStoreVenue(store.state, venueId)) return ''
  let allowed = takeawayAllowed
  if (!orderContext && takeawayAllowed === undefined && Object.prototype.hasOwnProperty.call(query || {}, 'ta')) {
    allowed = await takeawayAllowedFromVenue(store.state.venueId)
    // 问门店设置期间换了店 ⇒ 什么都不动（同上面的门店守卫）
    if (!isStoreVenue(store.state, venueId)) return ''
  }
  const state = store.state
  const storeVenueId = state.venueId
  const previous = state.menuId
  const stateContext = menuContextFromState(state, { venueId: storeVenueId, query, table, takeawayAllowed: allowed, ...menuEntry(state, route) })
  if (!stateContext) return ''
  const context = orderContext ? buildQrMenuContext(orderContext) : stateContext
  store.commit('saveMenuId', '')
  try {
    const res = await getMenu({ venueId: storeVenueId, context })
    const menuId = resolvedMenuIdOf(res)
    if (store.state.venueId === storeVenueId) {
      store.commit('saveMenuId', menuId)
      rememberLinkTables(storeVenueId, context ? context.menuLink : undefined, res)
    }
    return menuId
  } catch (error) {
    console.log(error)
    if (store.state.venueId === storeVenueId) {
      if (!store.state.menuId) store.commit('saveMenuId', previous)
      rememberLinkTables(storeVenueId, undefined, undefined)
    }
    return ''
  }
}
